import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LoggedInCallback } from '../../service-auth/cognito.service';
import { RoleChangeListener, StartupService } from '../../service-ui/startup.service';
import { MatSidenav } from '@angular/material/sidenav';
import { MediaMatcher } from '@angular/cdk/layout';
import { Subject } from 'rxjs';
import { LanguagePipe } from '../../pipe/language.pipe';
import {
  SalesTaxConfig,
  SalesTaxExemptionService,
} from '../../service-data/sales-tax-exemption.service';
import { ServiceProviderService } from '../../service-data/service-provider.service';

/**
 * ServiceProvider-role settings host.
 *
 * Currently houses the Sales Tax configuration (nexus + reciprocity)
 * and the exemption review queue scoped to the SP. The structure
 * matches `NetworkSettingsComponent` minus the SP picker — the
 * `serviceProviderId` is fixed to `startupService.selectedRoleId`.
 */
@Component({
  selector: 'app-service-provider-settings',
  templateUrl: './service-provider-settings.component.html',
  styleUrls: ['./service-provider-settings.component.css'],
})
export class ServiceProviderSettingsComponent
  implements OnDestroy, OnInit, LoggedInCallback, AfterViewInit, RoleChangeListener {
  @ViewChild('mainSideNav', { static: true }) mainSideNav: MatSidenav;
  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;
  onDestroySubject: Subject<boolean> = new Subject();

  /** Active SP id, derived from the selected role. */
  spId: number | null = null;

  /**
   * SP's mapped CollectionProvider id (from the SP row's
   * `collectionprovider_ID`). Resolved once the SP id is known — we
   * fetch the SP row via {@link ServiceProviderService.getServiceProvider}
   * because the server-side authorization on that endpoint scopes an
   * `serviceProvider`-role caller to its own row (see the
   * ServiceProviderRoutes gating), so the fetch is safe from this
   * context. Null when the SP has no CP mapping — the template hides
   * the editor and shows a hint.
   */
  spCollectionProviderId: number | null = null;
  spCollectionProviderLoadError: string = '';

  // Sales Tax panel state (mirrors the NetworkSettings shape, without the SP picker)
  salesTaxConfig: SalesTaxConfig | null = null;
  salesTaxConfigLoading = false;
  salesTaxConfigSaving = false;
  salesTaxConfigError = '';
  stxNexusStates: string[] = [];
  stxReciprocityOther: string[] = [];
  stxReciprocityWholesale: string[] = [];

  constructor(
    public router: Router,
    public startupService: StartupService,
    private salesTaxExemptionService: SalesTaxExemptionService,
    private serviceProviderService: ServiceProviderService,
    public changeDetectorRef: ChangeDetectorRef,
    public media: MediaMatcher
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addEventListener('change', this._mobileQueryListener);
    this.startupService.addRoleChangeListener(this);
  }

  ngOnInit() {
    this.startupService.isAuthenticated(this);
    this.resolveSpId();
  }

  ngAfterViewInit() {}

  ngOnDestroy() {
    this.onDestroySubject.next(true);
    this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      this.router.navigate(['/home/login']);
    }
  }

  roleChanged(role: string, roleId: string): void {
    this.resolveSpId();
  }

  private resolveSpId(): void {
    if (this.startupService.selectedRole === 'serviceProvider'
        && this.startupService.selectedRoleId) {
      const n = Number(this.startupService.selectedRoleId);
      this.spId = Number.isFinite(n) ? n : null;
      if (this.spId != null) {
        this.loadSalesTaxConfig();
        this.loadServiceProviderCollectionProviderId();
      }
    } else {
      this.spId = null;
      this.spCollectionProviderId = null;
      this.spCollectionProviderLoadError = '';
    }
  }

  /**
   * Fetch this SP's own row so we know its `collectionprovider_ID`
   * to feed into the CollectionProvider editor. The GET is scoped by
   * the server to the caller's SP (per ServiceProviderRoutes gating);
   * failures collapse to "no editor rendered" rather than blowing up
   * the page (Sales Tax stays functional independently).
   */
  private loadServiceProviderCollectionProviderId(): void {
    if (this.spId == null) { return; }
    this.spCollectionProviderId = null;
    this.spCollectionProviderLoadError = '';
    this.serviceProviderService.getServiceProvider(String(this.spId)).subscribe(
      sp => {
        if (!sp) {
          this.spCollectionProviderLoadError = LanguagePipe.get(
            'Could not load your ServiceProvider record.');
          return;
        }
        this.spCollectionProviderId = sp.collectionprovider_ID || null;
      },
      err => {
        console.error('ServiceProviderSettings: getServiceProvider failed', err);
        this.spCollectionProviderLoadError = this.errorMessage(err,
          LanguagePipe.get('Could not load your ServiceProvider record.'));
      }
    );
  }

  // ── Sales Tax config ──────────────────────────────────────────────

  loadSalesTaxConfig(): void {
    if (this.spId == null) { return; }
    this.salesTaxConfigLoading = true;
    this.salesTaxConfigError = '';
    this.salesTaxExemptionService.getSpConfig(this.spId).subscribe(
      cfg => {
        this.salesTaxConfig = cfg;
        this.stxNexusStates = (cfg.nexusStates || []).slice();
        this.stxReciprocityOther = (cfg.reciprocityOther || []).slice();
        this.stxReciprocityWholesale = (cfg.reciprocityWholesale || []).slice();
        this.salesTaxConfigLoading = false;
      },
      err => {
        console.error('ServiceProviderSettings: getSpConfig failed', err);
        this.salesTaxConfigLoading = false;
        this.salesTaxConfigError = this.errorMessage(err,
          LanguagePipe.get('Failed to load Sales Tax configuration.'));
      }
    );
  }

  saveSalesTaxConfig(): void {
    if (this.spId == null) { return; }
    this.salesTaxConfigSaving = true;
    this.salesTaxConfigError = '';
    this.salesTaxExemptionService.saveSpConfig(this.spId, {
      nexusStates: this.stxNexusStates,
      reciprocityOther: this.stxReciprocityOther,
      reciprocityWholesale: this.stxReciprocityWholesale,
    }).subscribe(
      cfg => {
        this.salesTaxConfig = cfg;
        this.salesTaxConfigSaving = false;
      },
      err => {
        console.error('ServiceProviderSettings: saveSpConfig failed', err);
        this.salesTaxConfigSaving = false;
        this.salesTaxConfigError = this.errorMessage(err,
          LanguagePipe.get('Failed to save Sales Tax configuration.'));
      }
    );
  }

  private errorMessage(err: any, fallback: string): string {
    if (err && err.error && typeof err.error === 'object' && err.error.error) {
      return String(err.error.error);
    }
    if (err && err.message) { return String(err.message); }
    return fallback;
  }
}

